import React, { useState, useEffect, useCallback } from 'react';
import { Collapse, Select, Form } from 'antd';
import gql from 'graphql-tag';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { useApolloClient } from '@apollo/client';
import { queryUserGroupsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/userGroups/';
import fileIcon from '../../_shared/assets/erin_lightgray.png';
import { downloadFromS3Signed } from '../../_shared/services/utils.js';
import { measurements } from './commonMeasurement.js';

const { Option } = Select;

const GroupMeasurementComponent = (props) => {
	const {
		currentUser,
		onUpdateUserGroup,
		setCurrentUserGroup,
	} = props;

	const client = useApolloClient();

	// State initialization
	const [state, setState] = useState({
		defaultActiveUserGroupKey: '',
		company: get(currentUser, 'company'),
		theme: JSON.parse(get(currentUser, 'company.theme', '{}')),
		userGroup: get(currentUser, 'userGroup'),
		userGroups: [],
		errorImageSrc: null,
	});

	// Helper to update state
	const updateState = useCallback((updates) => {
		setState(prevState => ({ ...prevState, ...updates }));
	}, []);

	// Get user groups with pagination support
	const getUserGroups = useCallback(async (
		policy = 'network-only',
		nextToken = null,
		allUserGroups = []
	) => {
		try {
			const { data } = await client.query({
				query: gql(queryUserGroupsByCompanyIdIndex),
				variables: {
					companyId: get(currentUser, 'companyId'),
					after: nextToken,
				},
				fetchPolicy: policy,
			});
			
			const userGrps = [...data.queryUserGroupsByCompanyIdIndex.items];
			const token = get(data, 'queryUserGroupsByCompanyIdIndex.nextToken', null);
			allUserGroups = [...allUserGroups, ...userGrps];
			
			updateState({ userGroups: allUserGroups });
			
			if (token) {
				getUserGroups(policy, token, allUserGroups);
			}
		} catch (error) {
			console.error('Error fetching user groups:', error);
		}
	}, [client, currentUser, updateState]);

	// Initialize component
	useEffect(() => {
		const initializeComponent = async () => {
			// Fetch user groups
			getUserGroups();

			// Load error image if available
			const errorImage = get(state.company, 'errorImage.key', false);
			if (errorImage) {
				const errorImageSource = await downloadFromS3Signed(errorImage, 'erin-images');
				updateState({ errorImageSrc: errorImageSource });
			}
		};

		initializeComponent();
	}, [getUserGroups, state.company]);

	// Handle measurement selection
	const handleSelectMeasurement = useCallback((value, userGroupId) => {
		const { userGroup } = state;

		onUpdateUserGroup({
			input: {
				id: userGroupId,
				measurement: value,
			},
		});

		const currentUserGroupId = get(currentUser, 'userGroupId');
		if (currentUserGroupId === userGroupId) {
			const updatedUserGroup = {
				...userGroup,
				id: userGroup.id,
				measurement: value,
			};
			setCurrentUserGroup(updatedUserGroup);
		}
	}, [state.userGroup, currentUser, onUpdateUserGroup, setCurrentUserGroup]);

	// Handle language selection
	const handleSelectLanguage = useCallback((value, userGroupId) => {
		return onUpdateUserGroup({
			input: {
				id: userGroupId,
				languageCode: value,
			},
		});
	}, [onUpdateUserGroup]);

	// Language options
	const languageOptions = [
		{ key: "US", value: "US", label: "English" },
		{ key: "AR", value: "AR", label: "اَلْفُصْحَىٰ" },
		{ key: "CS", value: "CS", label: "Čeština" },
		{ key: "DE", value: "DE", label: "Deutsch" },
		{ key: "ES", value: "ES", label: "Español" },
		{ key: "FR", value: "FR", label: "Français" },
		{ key: "FR-CA", value: "FR-CA", label: "Français (Canada)" },
		{ key: "IT", value: "IT", label: "Italiano" },
		{ key: "HU", value: "HU", label: "Magyar" },
		{ key: "NL", value: "NL", label: "Nederlands" },
		{ key: "PL", value: "PL", label: "Polski" },
		{ key: "PT", value: "PT", label: "Português" },
		{ key: "PT-BR", value: "PT-BR", label: "Português (Brasil)" },
		{ key: "TL", value: "TL", label: "Tagalog" },
		{ key: "TR", value: "TR", label: "Türkçe" },
		{ key: "RU", value: "RU", label: "Русский" },
		{ key: "ZH-CN", value: "ZH-CN", label: "简体中文" },
		{ key: "JA", value: "JA", label: "日本語" },
	];

	// Render user groups
	const renderUserGroups = useCallback(() => {
		const { userGroups } = state;
		
		const measurementOptions = measurements.map((measurement) => (
			<Option key={measurement.id}>
				{measurement.name}
			</Option>
		));

		const languageOptionComponents = languageOptions.map((lang) => (
			<Select.Option key={lang.key} value={lang.value}>
				{lang.label}
			</Select.Option>
		));

		// Sort userGroups by name
		const sortedGroups = orderBy(userGroups, ['name'], ['asc']);

		return (
			<Collapse
				className="bonus-collapse"
				bordered={false}
				expandIconPosition="end"
			>
				<Collapse.Panel
					key="measurement"
					className="bonus-collapse-card"
					extra={
						<div className="bonus-collapse-icon-wrap">
							<i className="icon-arrow-down bonus-collapse-icon" />
						</div>
					}
					header={<h4>Group Setting</h4>}
					onClick={() => updateState({ defaultActiveUserGroupKey: 'measurement' })}
				>
					{sortedGroups
						.filter((group) => group.active === true)
						.map((item) => (
							<div key={item.id} className="bs-form">
								<div className="bs-form-grp">
									<div className="custom-form-group">
										<label className="bs-label bs-label-width">
											{item.name}:
										</label>
										<Select
											style={{ width: 180 }}
											className="custom-input"
											defaultValue={get(item, 'measurement')}
											placeholder="Select"
											onSelect={(value) =>
												handleSelectMeasurement(value, item.id)
											}
										>
											{measurementOptions}
										</Select>
									</div>
									<div className="custom-form-group">
										<label className="bs-label bs-label-width">
											Default Language:
										</label>
										<Select
											className="custom-input"
											defaultValue={item.languageCode}
											placeholder="Select"
											dropdownMatchSelectWidth={false}
											onSelect={(value) =>
												handleSelectLanguage(value, item.id)
											}
										>
											{languageOptionComponents}
										</Select>
									</div>
								</div>
							</div>
						))}
				</Collapse.Panel>
			</Collapse>
		);
	}, [state, handleSelectMeasurement, handleSelectLanguage, updateState, languageOptions]);

	// Render list with no content fallback
	const renderList = useCallback((userGroups) => {
		const { company, defaultActiveUserGroupKey, errorImageSrc } = state;
		const whiteLabel = get(company, 'whiteLabel');

		if (userGroups.length === 0) {
			return (
				<div className="no-content">
					{whiteLabel ? (
						<img
							src={errorImageSrc}
							alt="error image"
							className="no-content-icon"
						/>
					) : (
						<img alt="erin-logo" className="no-content-icon" src={fileIcon} />
					)}
					<p className="no-content-text">No User Group have been created. </p>
				</div>
			);
		}

		if (userGroups && userGroups.length > 0 && defaultActiveUserGroupKey) {
			return (
				<Collapse
					bordered={false}
					activeKey={defaultActiveUserGroupKey}
					className="bonus-collapse"
					expandIconPosition="end"
				>
					{renderUserGroups()}
				</Collapse>
			);
		}

		if (userGroups && userGroups.length > 0 && !defaultActiveUserGroupKey) {
			return (
				<Collapse
					bordered={false}
					className="bonus-collapse"
					expandIconPosition="end"
				>
					{renderUserGroups()}
				</Collapse>
			);
		}
	}, [state, renderUserGroups]);

	// Main render
	const { userGroups } = state;
	
	return (
		<Form>
			{renderList(userGroups)}
		</Form>
	);
};

export const GroupMeasurement = GroupMeasurementComponent;
